import {
  Points,
  BufferGeometry,
  PointsMaterial,
  BufferAttribute,
  Vector3,
  Raycaster,
} from "three";
import { load } from "@loaders.gl/core";
import { LASLoader } from "@loaders.gl/las";
import { PLYLoader } from "@loaders.gl/ply";
import { RaycasterSettings } from "../config/ViewerConfig";
import axios from "axios";
import { unzip } from "unzipit";
import { PointCloudType } from "../pointcloud/loader/interface";
import { DEFAULT_POINT_SIZE } from "../config/ViewerConfig";

export class PointCloud {
  private _points: Points;
  private _geometry: BufferGeometry;
  private _material: PointsMaterial;
  private _offset: Vector3;
  // private _raycaster: Raycaster;
  private _threshold: number;

  constructor(
    raycasterSettings: RaycasterSettings
  ) {
    this._points = new Points();
    this._geometry = new BufferGeometry();
    this._material = new PointsMaterial({
      vertexColors: true,
      size: DEFAULT_POINT_SIZE,
      sizeAttenuation: false,
    });
    // this._raycaster = new Raycaster();
    this._threshold = raycasterSettings.threshold;
    this._offset = new Vector3(0, 0, 0);
  }

  public getPointsMaterial(): PointsMaterial {
    return this._material;
  }

  public getMesh(): Points {
    return this._points;
  }

  public getOffset(): Vector3 {
    return this._offset;
  }

  public loadPointCloud(
    url: string,
    type: PointCloudType,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, rejects) => {
      let result = null;
      switch (type) {
        case PointCloudType.LAS:
          result = await this._loadLAS(url, abortController)
            .then(() => resolve())
            .catch(() => rejects());
          break;
        case PointCloudType.PLY:
          result = await this._loadPLY(url, abortController)
            .then(() => resolve())
            .catch(() => rejects());
          break;
        default:
          console.error("This format is not supported");
          rejects();
          break;
      }
    });
  }

  nf = (num: number) =>
    num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  logFileSize = async (name: string, data: Promise<Blob | undefined>) => {
    const lasData = await data;
  };

  getZipResponse: (
    url: string,
    abortController: AbortController
  ) => Promise<Blob | undefined> = async (
    url: string,
    abortController: AbortController
  ) => {
    let lasData: Promise<Blob | undefined>;
    try {
      await axios
        .get(url, {
          withCredentials: true,
          responseType: "blob",
          signal: abortController.signal,
        })
        .then(async (res) => {
          const { entries } = await unzip(res.data);
          Object.entries(entries).forEach(([name, entry]) => {
            // １個ファイルだけ の想定である！
            lasData = entry.blob();
            this.logFileSize(name, lasData);
          });
        });
    } catch (e) {}

    return lasData;
  };

  private async _loadLAS(
    url: string,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, rejects) => {
      let unzipData = await this.getZipResponse(url as string, abortController);
      if (!unzipData) {
        rejects();
        return;
      }

      const data = await load(unzipData, LASLoader, {
        fetch: { credentials: "include", signal: abortController.signal },
        las: { colorDepth: "auto", skip: 1, fp64: true },
      }).catch((res) => {
        rejects();
      });
      let filtered = new Float32Array(
        (data.attributes.COLOR_0.value.length * 3) / 4
      ); // R, G, B, alpha でくるデータからR, G, Bを抽出
      let j = 0;
      for (let i = 0; i < data.attributes.COLOR_0.value.length; i++) {
        if ((i + 1) % 4 !== 0) {
          // R, G, B, alpha で来るのでalphaをskip
          filtered[j] = data.attributes.COLOR_0.value[i] / 255; // 0-255で来るRGBを0-1に変換
          j++;
        }
      }
      /* position情報*/
      /* pointOffsetの設定-> 元データのtranslate*/
      this._offset = new Vector3(Infinity, Infinity, Infinity);
      for (let i = 0; i < data.attributes.POSITION.value.length; i += 3) {
        this._offset.x =
          data.attributes.POSITION.value[i] < this._offset.x
            ? data.attributes.POSITION.value[i]
            : this._offset.x;
        this._offset.y =
          data.attributes.POSITION.value[i + 1] < this._offset.y
            ? data.attributes.POSITION.value[i + 1]
            : this._offset.y;
        this._offset.z =
          data.attributes.POSITION.value[i + 2] < this._offset.z
            ? data.attributes.POSITION.value[i + 2]
            : this._offset.z;
      }
      for (let i = 0; i < data.attributes.POSITION.value.length; i += 3) {
        data.attributes.POSITION.value[i] -= this._offset.x;
        data.attributes.POSITION.value[i + 1] -= this._offset.y;
        data.attributes.POSITION.value[i + 2] -= this._offset.z;
      }
      /* Float64 -> Float32に変換*/
      const float32Array = new Float32Array(
        data.attributes.POSITION.value.length
      );
      for (let i = 0; i < data.attributes.POSITION.value.length; i++) {
        float32Array[i] = data.attributes.POSITION.value[i];
      }
      /* 変換してからBufferAttributeにセット*/
      this._geometry.setAttribute(
        "position",
        new BufferAttribute(float32Array, data.attributes.POSITION.size)
      );
      this._geometry.setAttribute("color", new BufferAttribute(filtered, 3));

      this._points.geometry = this._geometry;
      this._points.material = this._material;
      this._points.frustumCulled = false;

      this._points.geometry.computeBoundingBox();
      this._points.geometry.computeBoundingSphere();

      resolve();
    });
  }

  private async _loadPLY(
    url: string,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, rejects) => {
      let unzipData = await this.getZipResponse(url as string, abortController);
      if (!unzipData) {
        rejects();
        return;
      }

      const data = await load(unzipData, PLYLoader, {
        fetch: { credentials: "include", signal: abortController.signal },
      }).catch((res) => {
        rejects();
      });

      let filtered = new Float32Array(data.attributes.COLOR_0.value.length); // データからR, G, Bを抽出
      for (let i = 0; i < data.attributes.COLOR_0.value.length; i++) {
        filtered[i] = data.attributes.COLOR_0.value[i] / 255; // 0-255で来るRGBを0-1に変換
      }
      this._geometry.setAttribute(
        "position",
        new BufferAttribute(
          data.attributes.POSITION.value,
          data.attributes.POSITION.size
        )
      );
      this._geometry.setAttribute("color", new BufferAttribute(filtered, 3));

      this._points.geometry = this._geometry;
      this._points.material = this._material;
      this._points.frustumCulled = false;

      this._points.geometry.computeBoundingBox();
      this._points.geometry.computeBoundingSphere();

      resolve();
    });
  }

  public intersect(raycaster: Raycaster): Vector3 {
    const result = new Vector3();
    // const coords = new Vector2();

    // e.preventDefault();
    // coords.copy(this.CalculateMousePosition(e, canvas));
    if (raycaster.params.Points !== undefined) {
      raycaster.params.Points.threshold = this._threshold;
    }
    // this._raycaster.setFromCamera(coords, camera);
    let intersects = raycaster.intersectObject(this._points);


    this._points.geometry.computeBoundingBox();
    this._points.geometry.computeBoundingSphere();
    if (intersects.length === 0) return null;
    // return result.set(
    //   intersects[0].point.x + this._offset.x,
    //   intersects[0].point.y + this._offset.y,
    //   intersects[0].point.z + this._offset.z
    // );
    //offset is already taken care of
    return result.set(
      intersects[0].point.x,
      intersects[0].point.y,
      intersects[0].point.z
    );
  }
}
